import { Disclosure, DisclosureButton, DisclosurePanel } from '@headlessui/react'
import { ArrowPathIcon } from '@heroicons/react/20/solid'
import * as _ from 'lodash-es'
import PropTypes from 'prop-types'
import React, { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'

import { CheckBox } from '@/components/forms/CheckBox'
import { AddIcon, MinusIcon } from '@/components/icons'
import { Slideout } from '@/components/ui/Slideout'
import { updateUser } from '@/services/user.service'
import { toast } from '@/utils/helpers'

const DealerSettings = ({
  dealerAccess,
  loading: loadingDealerAccess,
  setShow,
  settings,
  setCurrentUser,
  userId,
}) => {
  // State
  const [loading, setLoading] = useState(false)

  const handleSuccesses = (message) => toast(message, 'success')
  const handleErrors = (message) => toast(message, 'error')

  const { setValue, handleSubmit, reset, watch } = useForm({
    dealerNotificationsEnabled: [],
  })

  useEffect(() => {
    reset({
      dealerNotificationsEnabled: settings?.dealerNotificationsEnabled || [],
    })
  }, [])

  return (
    <Slideout
      content={
        <div className="relative mt-6 flex flex-1 flex-col gap-4 px-4 sm:px-6">
          {loadingDealerAccess ? (
            <div className="flex h-full w-full flex-col items-center justify-center space-y-2">
              <span className="text-2xl font-bold">Loading...</span>

              <span className="flex items-center pr-3">
                <div className="h-10 w-10">
                  {/* eslint-disable-next-line tailwindcss/no-custom-classname, tailwindcss/classnames-order */}
                  <svg className="motion-safe:animate-spin-slow h-10 w-10" viewBox="0 0 40 40">
                    <ArrowPathIcon className="h-10 w-10" aria-hidden="true" />
                  </svg>
                </div>
              </span>
            </div>
          ) : (
            _.map(dealerAccess, (groupData) => (
              <Disclosure as="div" defaultOpen={false} key={groupData.dealerGroup}>
                {({ open }) => (
                  <div>
                    <span className="flex w-full items-center justify-between">
                      <DisclosureButton className="flex w-full flex-row items-center justify-between rounded-lg pr-4 text-xl font-bold leading-none">
                        <h3 className="text-lg font-semibold">{groupData.dealerGroup}</h3>

                        {open ? (
                          <MinusIcon className="h-4 w-4 stroke-gray-700" />
                        ) : (
                          <AddIcon className="h-4 w-4 stroke-gray-700" />
                        )}
                      </DisclosureButton>
                    </span>

                    <DisclosurePanel className="mt-2">
                      {groupData.dealers.length > 1 && (
                        <CheckBox
                          className="mb-2"
                          key={`select-all-${groupData.dealerGroup}`}
                          label="Select All"
                          name="selectAll"
                          onChange={(event) => {
                            const currentValues = watch('dealerNotificationsEnabled')

                            let newValues = []
                            if (event.target.checked) {
                              newValues = [...currentValues, ..._.map(groupData.dealers, 'id')]
                            } else {
                              newValues = currentValues.filter(
                                (id) => !_.includes(_.map(groupData.dealers, 'id'), id),
                              )
                            }

                            setValue('dealerNotificationsEnabled', newValues)
                          }}
                          value={_.every(groupData.dealers, (dealer) =>
                            watch('dealerNotificationsEnabled')?.includes(dealer.id),
                          )}
                        />
                      )}

                      {_.map(groupData.dealers, (dealer) => (
                        <div key={dealer.id} className="mb-2">
                          <CheckBox
                            value={watch('dealerNotificationsEnabled')?.includes(dealer.id)}
                            label={dealer.name}
                            name={dealer.id}
                            onChange={(event) => {
                              const currentValues = watch('dealerNotificationsEnabled')
                              const newValues = event.target.checked
                                ? [...currentValues, dealer.id]
                                : currentValues.filter((id) => id !== dealer.id)

                              setValue('dealerNotificationsEnabled', newValues)
                            }}
                          />
                        </div>
                      ))}
                    </DisclosurePanel>
                  </div>
                )}
              </Disclosure>
            ))
          )}
        </div>
      }
      description="Control which dealers you receive notifications for. These settings apply to both In-App and Email notifications."
      loading={loading}
      open
      onSave={handleSubmit(async (data) => {
        await updateUser(
          {
            id: userId,
            notificationSettings: {
              dealerNotificationsEnabled: data.dealerNotificationsEnabled || [],
            },
          },
          handleErrors,
          setLoading,
          () => {
            handleSuccesses('Dealer notification settings updated successfully.')
            setShow(false)
          },
          setCurrentUser,
        )
      })}
      setOpen={setShow}
      title="Limit Notifications by Dealer"
    />
  )
}

DealerSettings.propTypes = {
  dealerAccess: PropTypes.object.isRequired,
  loading: PropTypes.bool.isRequired,
  setShow: PropTypes.func.isRequired,
  settings: PropTypes.object,
  setCurrentUser: PropTypes.func.isRequired,
  userId: PropTypes.string.isRequired,
}

export default DealerSettings
